import type { ReactNode } from 'react';
import { cn } from '@web/lib/cn';

/**
 * Empty, loading and error states.
 *
 * Deliberately not cute. An empty parked queue means nobody is waiting, which
 * is the target state of the whole product — an illustration and a joke about
 * inbox zero would make the operator look twice at a screen that is telling
 * them everything is fine. A rule, a count, and the facts.
 */
export function EmptyState({
  headline,
  detail,
  action,
}: {
  headline: string;
  detail?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex h-full items-start justify-center px-6 py-16">
      <div className="w-full max-w-sm border-t border-rule pt-4">
        <div className="font-mono text-[11px] tracking-[0.1em] text-ink-faint">STATUS</div>
        <h2 className="mt-2 text-sm font-semibold text-ink">{headline}</h2>
        {detail !== undefined && <p className="mt-1 text-xs text-ink-dim">{detail}</p>}
        {action !== undefined && <div className="mt-4">{action}</div>}
      </div>
    </div>
  );
}

/** A hairline progress bar, not a spinner. Nothing in this console rotates. */
export function LoadingBar({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="flex h-full items-start px-4 py-4" role="status" aria-label={label}>
      <div className="h-px w-full max-w-xs bg-rule-strong" />
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  return (
    <div className="flex h-full items-start justify-center px-6 py-16">
      <div className="w-full max-w-sm border-t-2 border-signal pt-4">
        <div className="font-mono text-[11px] tracking-[0.1em] text-signal">FAILED</div>
        <p className="mt-2 text-xs text-ink">{message}</p>
        {onRetry !== undefined && (
          <button
            type="button"
            onClick={onRetry}
            className="mt-4 h-8 border border-rule-strong px-3 text-xs hover:bg-hover"
          >
            Try again
          </button>
        )}
      </div>
    </div>
  );
}

export function SectionLabel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        'font-mono text-[10px] tracking-[0.12em] text-ink-faint uppercase select-none',
        className,
      )}
    >
      {children}
    </div>
  );
}
